import {useCallback, useEffect, useState} from 'react';
import {createSearchParams, URLSearchParamsInit} from "react-router-dom";
import {useTheCellMapContext} from "../contexts/TheCellMapContext";
import {AppSettings} from "../settings";
import {AlertType, FigureType, RequestType, RowModalType} from "../types/enums";
import {
  Correlations,
  EnrichmentResult,
  FigureResult,
  LibraryModalResult,
  QueryModalResult,
  Scores
} from "../types/list";
import {OverlayExample, OverlayResults} from "../types/overlay";

import {
  Annotation,
  Dataset,
  DataStatus,
  GeneInfo,
  GeneRefs,
  Region,
  RequestData,
  SubNetworkResult
} from "../types/Types";
import {apiGetUrl} from "../utils/request";


/**
 * Constructs a URL for the dataset file using the provided file name. Automatically appends .json suffix if
 * parameter is not provided.
 *
 * @param fileName - The name of the dataset file without the extension.
 * @param [suffix='.json'] - The file extension (default is 'json').
 * @return The full URL pointing to the dataset file on the static server.
 */
function datasetUrl(fileName: string, suffix: string = '.json'): string {
  return `${AppSettings.STATIC_SERVER}/${AppSettings.DATASET}/${fileName}${suffix}`;
}

/**
 * Creates a short hash of the POST data, used as a cache key.
 */
function shortHash(data: any): string {
  const json = JSON.stringify(data);
  let h = 0;
  for (let i = 0; i < json.length; i++) {
    h = (h * 31 + json.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36); // short alphanumeric
}

/**
 * A mapping of file identifiers to their corresponding promises.
 * Each key in the record represents a unique identifier for a file,
 * and the value is either a promise that resolves to the file's associated data
 * or `undefined` if no promise is associated with the key. This is used to prevent
 * duplicate file fetches and ensure that only one fetch operation is active for each file.
 */
const filePromises: Record<string, Promise<any> | undefined> = {};
const filesCache: Record<string, unknown> = {};

/**
 * Main fetching function. It fetches the data from the provided URL and caches it in the filePromises object.
 * Decides whether to use POST or GET request based on the data parameter.
 *
 * @param url - Full URL to fetch data from.
 * @param cacheKey - Unique key to identify the file in the filePromises object.
 * @param startLoading - Pre-loading callback
 * @param stopLoading - Post-loading callback
 * @param onError - Error callback
 * @param data - Data to be sent in the POST request, if any.
 * @returns A promise that resolves to the fetched data of type T, or undefined if the fetch operation is already in progress.
 */
function fetchAndCache<T>(
  url: string,
  cacheKey: string,
  startLoading: () => void,
  stopLoading: () => void,
  onError: (error: string) => void,
  data?: any
): Promise<T | undefined> | undefined {
  const type = cacheKey.split('-')[0];

  if (!filePromises[cacheKey]) {
    startLoading();

    const options = data ? {
      method: "POST",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify(data),
    } : undefined;

    filePromises[cacheKey] = fetch(
      url,
      options
    ).then(res => {
      if (!res.ok) {
        console.error(`API call to ${url} failed with status ${res.status}: ${res.statusText}`);
        throw new Error(
          `Error fetching ${type} data from API.`,
          {cause: res}
        );
      }

      return res.json()
    }).then(json => {
      if ("error" in json) {
        console.error(`API call to ${url} failed with error: ${json["error"]}`);
        throw new Error(
          `Error fetching ${type} data from API: ${json["error"]}`,
          {cause: json}
        );
      }

      return json as Promise<T>;
    }).then(json => {
      filesCache[cacheKey] = json;
      return json;
    }).catch(error => {
      console.error(type, error);
      onError(error.message);
    }).finally(() => stopLoading());
  }
  return filePromises[cacheKey];
}

/**
 * A custom hook that fetches POST data from a specified URL and caches it in the application state.
 *
 * @param cacheKeyPrefix - Prefix to use for the cache key, suffixed with a short hash of the POST data.
 * @param url - The URL to fetch POST data from.
 * @param requestType - Get or Post request
 * @returns A function that takes POST data and returns a promise that resolves to the cached or fetched data.
 */
export function useRequestAndCache<T>(cacheKeyPrefix: string, url: string, requestType: RequestType) {
  const {startLoading, stopLoading, addAlert} = useTheCellMapContext();

  return useCallback((data: RequestData, globalLoading: boolean = true): Promise<T | undefined> => {
    const key = `${cacheKeyPrefix}-${shortHash(data)}`;

    // Return cached value immediately if present
    if (filesCache[key]) {
      return Promise.resolve(filesCache[key] as T);
    }

    let forwardedData: RequestData | null = data;
    let localUrl = url;

    /* If this is a GET request, we need to append the data to the URL and give null to fetching function. */
    if (requestType === RequestType.Get) {
      if (cacheKeyPrefix === RowModalType.Library || cacheKeyPrefix === RowModalType.Query) {
        const getParam = (data as { get_param: string | number }).get_param;
        localUrl = `${url}${getParam}/`;
      } else {
        localUrl = `${url}?${createSearchParams(data as URLSearchParamsInit).toString()}`;
      }
      forwardedData = null;
    }

    /* If we are in global loading mode, we need to increment the loading counter. */
    const onStart = globalLoading ? startLoading : () => {
    };
    const onStop = globalLoading ? stopLoading : () => {
    };
    const onError = (msg: string)=> {
      addAlert({
        type: AlertType.Error,
        message: msg
      });
    }

    // Otherwise fetch and cache
    return fetchAndCache<T>(localUrl, key, onStart, onStop, onError, forwardedData) as Promise<T | undefined>;
  }, [startLoading, stopLoading, url, cacheKeyPrefix]);
}

/**
 * Creates a custom React hook to manage the loading and caching of a JSON file associated with a specified key.
 *
 * @template T - The expected structure of the JSON data to be fetched.
 * @param key - The unique key used to identify and cache the JSON file in the state.
 * @param url - The URL from which the JSON file will be fetched.
 * @returns A React hook function that fetches the JSON file if not already present in the state and returns the
 *          cached or fetched data.
 */
const createUseFileHook = <T>(key: string, url: string) => {
  return () => {
    const {startLoading, stopLoading, addAlert} = useTheCellMapContext();
    const [data, setData] = useState<T | undefined>(filesCache[key] ? filesCache[key] as T : undefined);

    const onError = (msg: string)=> {
      addAlert({
        type: AlertType.Error,
        message: msg
      });
    }

    useEffect(() => {
      if (filesCache[key]) return;

      fetchAndCache<T>(url, key, startLoading, stopLoading, onError)
        ?.then(json => setData(json))
        .catch(err => {
          console.error("Fetch failed:", err);
          stopLoading();
        });
    }, [key, url, startLoading, stopLoading]);

    return data;
  };
};

/**
 * A method to create a custom hook for managing dataset JSON files.
 * It generates a hook using the provided file name and key.
 *
 * @param fileName - The name of the dataset file to be referenced.
 * @param [key=fileName] - Optional key used as an identifier for the hook. Defaults to the value of `fileName`.
 * @return A hook function for handling dataset JSON files.
 */
function createDatasetJsonHook<T>(fileName: string, key: string = fileName) {
  return createUseFileHook<T>(key, datasetUrl(fileName));
}

/**
 * A method to create a custom hook for managing JSON data using POST requests.
 *
 * @param requestType - The type of request to be made.
 * @param getType - The path to the /get/ endpoint on the scores server.
 * @return A hook function for handling JSON data via POST requests.
 */
function createDataJsonHook<T>(requestType: RequestType, getType: string) {
  return () =>
    useRequestAndCache<T>(getType, apiGetUrl(getType), requestType);
}

export function createFigureHook<T>() {
  return (figureType: FigureType) =>
    useRequestAndCache<T>(figureType, apiGetUrl(figureType), RequestType.Get);
}

/*
 * Pre-defined hooks for fetching and managing JSON files/data.
 */

/*
 * STATIC FILES
 */
export const useFileGenes = createDatasetJsonHook<GeneInfo[]>('genes');
export const useFileRefs = createDatasetJsonHook<GeneRefs[]>('refs');
export const useFileDataset = createDatasetJsonHook<Dataset>('dataset');
export const useFileRegions = createDatasetJsonHook<Region[]>('regions');
export const useFileAnnotations = createDatasetJsonHook<Annotation[]>('annotation');
export const useFileOverlayExamples = createUseFileHook<OverlayExample[]>(
  "safe_examples",
  `${process.env.PUBLIC_URL}/data/safe_examples.json`);

/*
 * OVERLAY REQUESTS
 */
export const usePostSafeCustom = createDataJsonHook<OverlayResults>(RequestType.Post, `safe_custom`);
export const usePostSafeScreen = createDataJsonHook<OverlayResults>(RequestType.Post, `safe_screen`);

export const usePostRiskCustom = createDataJsonHook<OverlayResults>(RequestType.Post, `risk_custom`);
export const usePostRiskScreen = createDataJsonHook<OverlayResults>(RequestType.Post, `risk_screen`);

/*
 * POST REQUESTS
 */
export const usePostListScores = createDataJsonHook<Scores>(RequestType.Post, `scores`);
export const usePostListCorrelations = createDataJsonHook<Correlations>(RequestType.Post, `correlations`);

export const usePostSubNetwork = createDataJsonHook<SubNetworkResult>(RequestType.Post, `correlations_network`);

export const usePostEnrichment = createDataJsonHook<EnrichmentResult>(RequestType.Post, `enrichment`);

/*
 * GET REQUESTS
 */
export const useGetListLibraryModal = createDataJsonHook<LibraryModalResult>(RequestType.Get, RowModalType.Library)
export const useGetListQueryModal = createDataJsonHook<QueryModalResult>(RequestType.Get, RowModalType.Query)

export const useDataStatus = createDataJsonHook<DataStatus>(RequestType.Get, `data_status`);

/*
 * PLOT REQUESTS
 * loading spinner is constrained to the plot container
 */
export const useFigure = createFigureHook<FigureResult>();

